import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { updateProjectColumns } from "store/features";

import { deleteGeomBins } from "api/data";

import { EntityKind } from "models/entityKind";

import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { usePurgeDeletedFieldsFromDefaults } from "components/user-settings/hooks";

import { useGroupByStore } from "../GroupByStoreProvider";
import { selectCheckedFields } from "../groupBySelectors";

// Geom bins currently only exist for Wells - see the getCategoryListWithUnit
// call below, which is hardcoded the same way.
const useMutationGeomBinDelete = (ids, projectId?: string, properties?: string[]) => {
  const dispatch = useDispatch();
  const { getCategoryListWithUnit } = useGetCategoryList();
  const purgeDeletedFieldsFromDefaults = usePurgeDeletedFieldsFromDefaults(
    EntityKind.Well
  );
  const checkedFields = useGroupByStore(selectCheckedFields);
  const setCheckedFields = useGroupByStore((s) => s.setCheckedFields);
  const { isLoading, mutate } = useMutation(
    () => {
      return deleteGeomBins(ids, projectId);
    },
    {
      onSuccess: () => {
        toast.success("Polygon bin was deleted.");
        // Geom bins currently only exist for Wells
        getCategoryListWithUnit(EntityKind.Well);
        setCheckedFields(
          checkedFields.filter((checkedItem) => !ids.includes(checkedItem))
        );
        if (projectId) {
          dispatch(updateProjectColumns());
        }

        // Best-effort background cleanup - must never block the rest of this
        // success handler if it fails for any reason (see
        // useMutationFieldDelete for the same pattern).
        try {
          purgeDeletedFieldsFromDefaults(properties ?? []);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to purge deleted bins from user defaults", error);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (err: any) => {
        if (err.response?.data) {
          toast.error(`${err?.response.data}`);
        } else {
          toast.error(
            "We're sorry a network error occurred. The bin could not be deleted."
          );
        }
      }
    }
  );

  return { isLoading, mutate };
};

export default useMutationGeomBinDelete;
